<template>
  <a-modal
    v-model:visible="visibility"
    :title="title"
    :closable="!loading"
    :maskClosable="!loading"
    centered
  >
    <a-spin :spinning="loading">
      <a-typography-title :level="5" style="margin-top: -15px">
        Completa los siguientes datos.
      </a-typography-title>
      <a-form
        name="form"
        layout="vertical"
        ref="formRef"
        :model="formState"
        :rules="rules"
        class="mt-3"
      >
        <a-form-item
          required
          name="requestEndDate"
          label="Fecha de solicitud de baja"
        >
          <a-date-picker
            format="DD-MM-YYYY"
            placeholder="Seleccione Fecha"
            v-model:value="formState.requestEndDate"
            :style="{ width: '100%' }"
          />
        </a-form-item>
        <a-form-item
          required
          name="endDate"
          label="Fecha de término de vigencia"
        >
          <a-date-picker
            format="DD-MM-YYYY"
            placeholder="Seleccione Fecha"
            v-model:value="formState.endDate"
            :style="{ width: '100%' }"
          />
        </a-form-item>
      </a-form>
    </a-spin>
    <template #footer>
      <a-button
        :loading="loading"
        key="back"
        :disabled="loading"
        @click="closeModal"
      >
        Cancelar
      </a-button>
      <a-button
        :loading="loading"
        key="submit"
        type="primary"
        :disabled="saveChangesButtonDisabled"
        @click="submitModal"
      >
        Confirmar
      </a-button>
    </template>
  </a-modal>
</template>

<script setup>
import { ref, reactive, computed, defineEmits, defineExpose } from 'vue';
import { useStore } from 'vuex';
import moment from 'moment';
import { normalizePicker, tryNormalizePicker } from '@/utils/calendarDay';
import notification from '@/utils/notifications';
import { PATCH_EMPLOYEE_DEPENDENT } from '@/store/types';

const emit = defineEmits(['patchedEmployeeDependent']);
const store = useStore();
const recordData = ref(null);
const loading = ref(false);
const visibility = ref(false);
const formRef = ref(null);

const title = ref('');

const formState = reactive({
  requestEndDate: null,
  endDate: null
});

const saveChangesButtonDisabled = computed(() => {
  if (!recordData.value) return true;

  return (
    JSON.stringify({
      endDate: formState.endDate ? tryNormalizePicker(formState.endDate) : null,
      requestEndDate: formState.requestEndDate
        ? tryNormalizePicker(formState.requestEndDate)
        : null
    }) ===
    JSON.stringify({
      endDate: recordData.value.endDate
        ? tryNormalizePicker(recordData.value.endDate)
        : null,
      requestEndDate: recordData.value.requestEndDate
        ? tryNormalizePicker(recordData.value.requestEndDate)
        : null
    })
  );
});

const rules = {
  requestEndDate: [
    {
      required: true,
      type: 'object',
      trigger: 'change',
      message: 'Seleccione fecha'
    }
  ],
  endDate: [
    {
      required: true,
      type: 'object',
      trigger: 'change',
      message: 'Seleccione fecha'
    }
  ]
};

// Null-safe preload: a bad record date leaves the field empty instead of
// throwing inside openModal and breaking the render. parseZone keeps the
// stored instant's offset so the displayed day (and every later
// normalizePicker read) is host-independent.
const preloadDate = (value) => {
  if (!value) return null;
  const iso = tryNormalizePicker(value);
  return iso ? moment.parseZone(iso) : null;
};

const openModal = (record) => {
  if (!record) return;

  recordData.value = record;
  visibility.value = true;
  title.value = `Dar de baja asociación ${record.fullName || ''} - ${record.coverageName || 'cobertura'}`;

  formState.requestEndDate = preloadDate(record.requestEndDate);
  formState.endDate = preloadDate(record.endDate);
};

const closeModal = () => {
  visibility.value = false;
  formRef.value?.resetFields();
};

const submitModal = () => {
  formRef.value?.validate().then(async (form) => {
    loading.value = true;
    try {
      await store.dispatch(PATCH_EMPLOYEE_DEPENDENT, {
        employeeCoverageId: recordData.value.employeeCoverageId,
        employeeCoverageDependentId:
          recordData.value.employeeCoverageDependentId,
        payload: Object.fromEntries(
          Object.entries(form).map(([field, value]) => [
            field,
            normalizePicker(value)
          ])
        )
      });
      notification(
        'success',
        'Correcto',
        'Asociación carga-cobertura dada de baja correctamente'
      );
      closeModal();
      emit('patchedEmployeeDependent');
    } catch (error) {
      notification('error', 'Error', error?.response?.data?.message);
    }
    loading.value = false;
  });
};

defineExpose({ openModal });
</script>
